<template>
  <lb-dialog
    :value="value"
    @input="$emit('input', $event)"
    heading="Data Processing Status"
    width="600"
    :loading="loading"
    data-testid="data-processing-status-dialog"
  >
    <template v-slot:body>
      <div class="dp-status-dialog" data-testid="data-processing-status-dialog__body">
        <div class="dp-status-dialog__toolbar">
          <v-tooltip bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click.stop="fetchData"
                data-testid="data-processing-status-dialog__refresh-btn"
              >
                <v-icon size="20">mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Refresh</span>
          </v-tooltip>
        </div>

        <v-simple-table dense class="dp-status-dialog__table" data-testid="data-processing-status-dialog__table">
          <template v-slot:default>
            <thead class="tableHeaderBg">
              <tr>
                <th class="dp-status-dialog__th">Collection Name</th>
                <th class="dp-status-dialog__th">Status</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in mainRows">
                <tr
                  :key="row.key"
                  class="dp-status-dialog__row"
                  data-testid="data-processing-status-dialog__row"
                >
                  <td class="dp-status-dialog__td">
                    <div
                      v-if="hasBatchData(row.value)"
                      class="d-flex align-center cursor-pointer"
                      @click.stop="toggleSubCategory(row.name)"
                      data-testid="data-processing-status-dialog__toggle"
                    >
                      <span class="dp-status-dialog__collection-name">{{ row.name }}</span>
                      <v-icon small class="ml-2">
                        {{ isExpanded(row.name) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                      </v-icon>
                    </div>
                    <span v-else class="dp-status-dialog__collection-name">{{ row.name }}</span>
                  </td>
                  <td class="dp-status-dialog__td">
                    <v-chip
                      label
                      small
                      :color="hasBatchData(row.value) ? 'info' : 'success'"
                      data-testid="data-processing-status-dialog__status-chip"
                    >
                      {{ hasBatchData(row.value) ? DATA_PROCESSING.STATUS.PENDING : DATA_PROCESSING.STATUS.COMPLETED }}
                    </v-chip>
                  </td>
                </tr>
                <tr
                  v-if="hasBatchData(row.value) && isExpanded(row.name)"
                  :key="'sub-' + row.name"
                  class="dp-status-dialog__sub-row"
                >
                  <td colspan="2" class="pa-0">
                    <v-simple-table dense class="dp-status-dialog__sub-table">
                      <template v-slot:default>
                        <thead class="tableHeaderBg">
                          <tr>
                            <th class="dp-status-dialog__sub-th">Batch Time</th>
                            <th class="dp-status-dialog__sub-th">Pending Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(batch, bKey) in row.value.batcheData"
                            :key="bKey"
                            class="dp-status-dialog__batch-row"
                            data-testid="data-processing-status-dialog__batch-row"
                          >
                            <td class="dp-status-dialog__sub-td">{{ formatDate(batch._id) }}</td>
                            <td class="dp-status-dialog__sub-td">{{ batch.count || 0 }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </td>
                </tr>
              </template>
              <template v-if="loading">
                <tr
                  v-for="n in 3"
                  :key="'skeleton-' + n"
                  class="dp-status-dialog__row"
                >
                  <td class="dp-status-dialog__td">
                    <v-skeleton-loader type="text" width="60%" />
                  </td>
                  <td class="dp-status-dialog__td">
                    <v-skeleton-loader type="chip" width="80px" />
                  </td>
                </tr>
              </template>
              <tr v-if="!loading && !hasData" data-testid="data-processing-status-dialog__empty">
                <td colspan="2" class="text-center dp-status-dialog__empty">
                  No data available
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import { DATA_PROCESSING } from "../utils/constants";
import helpers from '../utils/helpers';

export default {
  name: 'DataProcessingStatusDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      dataProcessingData: {},
      expandedsubbatches: [],
      DATA_PROCESSING
    }
  },
  computed: {
    filteredData() {
      const result = {};
      Object.keys(this.dataProcessingData || {}).forEach(key => {
        if (key !== '_id') result[key] = this.dataProcessingData[key];
      });
      return result;
    },
    hasData() {
      return Object.keys(this.filteredData).length > 0;
    },
    mainRows() {
      return Object.keys(this.filteredData).map(k => ({
        key: 'row-' + k,
        name: k,
        value: this.filteredData[k]
      }));
    }
  },
  mounted() {
    if (this.value) {
      this.fetchData();
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.fetchData();
      }
    }
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const dt = await helpers.apiGet.call(this, "/v2/varianceanalysis/dataprocessing/dataprocessingstatus");
        if (dt.data.status === "success") {
          this.dataProcessingData = dt.data.data[0] || {};
        } else {
          throw new Error("Error reading data");
        }
      } catch (err) {
        this.$store.commit("sbError", err.message || err || "Unknown error!");
        console.log('[DataProcessingStatusDialog]:', err);
      } finally {
        this.loading = false;
      }
    },
    hasBatchData(item) {
      return (item?.batcheData?.length || 0) > 0;
    },
    isExpanded(key) {
      return this.expandedsubbatches.indexOf(key) > -1;
    },
    toggleSubCategory(key) {
      const index = this.expandedsubbatches.indexOf(key);
      if (index === -1) this.expandedsubbatches.push(key);
      else this.expandedsubbatches.splice(index, 1);
    },
    formatDate(rawDate) {
      try {
        const date = new Date(rawDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('[DataProcessingStatusDialog]: Date format error', error.message);
        return rawDate;
      }
    }
  },
  beforeDestroy() {
    this.dataProcessingData = {};
    this.expandedsubbatches = [];
  }
}
</script>

<style lang="scss" scoped>
@import '../../../styles/tokens/typography';
@import '../../../styles/tokens/layout';

.dp-status-dialog {
  &__toolbar {
    display: flex;
    justify-content: flex-end;
    padding-bottom: var(--space-xs);
  }

  &__table {
    border: var(--border-sm) solid var(--color-border-default);
    border-radius: var(--radius-sm);
    overflow: hidden;
  }

  &__th {
    font-size: var(--font-size-xxs) !important;
    font-weight: 500 !important;
    color: var(--grey-800) !important;
    padding: var(--space-xs) var(--space-md) !important;
  }

  &__td {
    font-size: var(--font-size-xxs) !important;
    color: var(--grey-900) !important;
    padding: var(--space-xs) var(--space-md) !important;
    border-bottom: var(--border-sm) solid var(--color-border-light) !important;
  }

  &__collection-name {
    @include body-xxs-medium;
  }

  &__row:hover {
    background-color: var(--grey-50) !important;
  }

  &__row ::v-deep .v-skeleton-loader__text {
    height: var(--space-md);
    border-radius: var(--radius-xs);
    margin: 0;
  }

  &__row ::v-deep .v-skeleton-loader__chip {
    height: var(--space-lg);
    width: 5rem;
    border-radius: var(--radius-xs);
  }

  &__sub-row > td {
    padding: 0 !important;
  }

  &__sub-table {
    background-color: var(--grey-50) !important;
  }

  &__sub-th {
    font-size: var(--font-size-xxs) !important;
    font-weight: 500 !important;
    color: var(--grey-700) !important;
    padding: var(--space-2xs) var(--space-md) !important;
    background-color: var(--grey-75) !important;
  }

  &__sub-td {
    font-size: var(--font-size-xxs) !important;
    color: var(--grey-800) !important;
    padding: var(--space-2xs) var(--space-md) !important;
    border-bottom: var(--border-sm) solid var(--color-border-light) !important;
  }

  &__batch-row:last-child .dp-status-dialog__sub-td {
    border-bottom: none !important;
  }

  &__empty {
    font-size: var(--font-size-xs);
    color: var(--grey-500);
    padding: var(--space-lg) var(--space-md) !important;
  }
}
</style>